import { motion } from 'framer-motion';
import { ArrowUpRight, Star } from 'lucide-react';
import { TEAM } from '@/content/site';
import { TID } from '@/constants/testIds';

const ease = [0.16, 1, 0.3, 1];

export default function Team() {
  return (
    <section id="team" className="py-28 lg:py-36 bg-[hsl(var(--cream-2))]/70 border-y border-[hsl(var(--hairline))]/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease }}
          >
            <div className="text-overline mb-7">Our Team</div>
            <h2 className="font-display text-[1.75rem] sm:text-3xl lg:text-[2.5rem] leading-[1.15] max-w-2xl">
              Meet your
              <span className="italic text-[hsl(var(--primary))] font-medium"> lash specialists.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease, delay: 0.08 }}
            className="max-w-md text-[hsl(var(--muted-foreground))] leading-relaxed"
          >
            Three certified lash artists. One studio. Every lash lift at Prép is performed
            by a specialist trained in both YUMI Keratin and InLei Lash Filler® systems.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {TEAM.map((m, i) => (
            <motion.article
              key={m.id}
              data-testid={TID.teamCard(m.id)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease, delay: i * 0.1 }}
              className="group"
            >
              <div className="relative aspect-[4/5] rounded-[1.5rem] overflow-hidden bg-[hsl(var(--secondary))] mb-7 luxe-shadow">
                <img
                  src={m.img}
                  alt={`${m.name} — ${m.role} at Prép Beauty Parlour Vancouver`}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[hsl(var(--warm-ink))]/40 to-transparent pointer-events-none" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--cream))]/95 backdrop-blur px-3 py-1 text-[0.65rem] tracking-[0.22em] uppercase font-light">
                  <Star className="w-3 h-3 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" strokeWidth={1.25} />
                  {m.rating}
                </div>
              </div>
              <h3 className="font-display text-[1.7rem] lg:text-[2rem] tracking-tight">{m.name}</h3>
              <div className="mt-1.5 text-[0.65rem] uppercase tracking-[0.32em] text-[hsl(var(--primary))] font-light">
                {m.role}
              </div>
              <p className="mt-5 text-[0.92rem] text-[hsl(var(--muted-foreground))] leading-[1.75]">
                {m.bio}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {m.specialties.map((s) => (
                  <span
                    key={s}
                    className="text-[0.62rem] tracking-[0.24em] uppercase px-3 py-1 rounded-full border border-[hsl(var(--hairline))] text-[hsl(var(--muted-foreground))] font-light"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <a
                data-testid={TID.teamBookBtn(m.id)}
                href={m.book}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-1.5 text-[0.72rem] tracking-[0.24em] uppercase font-light text-[hsl(var(--primary))] hover:gap-3 transition-all duration-500"
              >
                Book with {m.name} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
              </a>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 hairline-divider" />
        <div className="mt-10 text-center text-[0.62rem] uppercase tracking-[0.32em] text-[hsl(var(--muted-foreground))] font-light">
          Combined 15+ years of lash lift expertise · YUMI & InLei certified
        </div>
      </div>
    </section>
  );
}
